import React from 'react';

import PropTypes from 'prop-types';
import styledC, { css, CSSObject } from 'styled-components';

import { useDeprecate, variantSwitch } from '@adsk/alloy-react-helpers';
import { RenderIconType } from '@adsk/alloy-react-icon';
import ProgressRing from '@adsk/alloy-react-progress-ring';
import { useTheme } from '@adsk/alloy-react-styles';
import {
  getInteractiveStyles,
  stylePropType,
  StyleProp,
} from '@adsk/alloy-react-theme';

import { buttonTypePropType, StylableButtonProps } from './consts';
import { buttonHasWidth } from './helpers';
import BasicButton from './templates/BasicButton';

const BUTTON_VARIANTS = {
  PRIMARY: 'primary',
  SECONDARY: 'secondary',
  TERTIARY: 'tertiary',
} as const;

type ButtonVariants = typeof BUTTON_VARIANTS;
type ButtonVariant = ButtonVariants[keyof ButtonVariants];

const BUTTON_SIZES = {
  SMALL: 'small',
  MEDIUM: 'medium',
} as const;

type ButtonSizes = typeof BUTTON_SIZES;
type ButtonSize = ButtonSizes[keyof ButtonSizes];

const ButtonLabel = styledC.div<{
  /**
   * Will truncate text when the button is constrained to a width
   */
  $isTruncated?: boolean;
  /**
   * Will hide rendered children when showing a full size spinner
   */
  $isFullSizeSpinner?: boolean;
  /**
   * Ellipsis styles from theme
   */
  $ellipsisStyle?: CSSObject;
}>`
  ${({ $isTruncated, $ellipsisStyle }) => $isTruncated && $ellipsisStyle}
  ${({ $isFullSizeSpinner }) =>
    $isFullSizeSpinner &&
    css`
      opacity: 0;
    `}
`;

export interface ButtonProps extends StylableButtonProps {
  variant?: ButtonVariant;
  size?: ButtonSize;
  renderIcon?: RenderIconType;
  renderRightIcon?: RenderIconType;
  disabled?: boolean;
  alert?: boolean;
  loading?: boolean;
  selected?: boolean;
  width?: string | number;
  target?: string; // Might have a better way to add those props based on `as` prop.
  href?: string;
  rel?: string;
}

/**
 * A button component which wraps an HTML button and adds to it the basic "btn" bootstrap class.
 * All arbitrary DOM properties and attributes (including event handlers) are accepted and passed to HTML button element.
 */
const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      style,
      className,
      children,
      variant = BUTTON_VARIANTS.SECONDARY,
      size = BUTTON_SIZES.MEDIUM,
      renderIcon,
      renderRightIcon,
      disabled = false,
      alert = false,
      loading = false,
      selected = false,
      type,
      ...props
    },
    ref,
  ) => {
    const theme = useTheme();
    const button = theme.tokens?.component?.button;

    useDeprecate(!!props.width, 'Button', 'width', 'style');
    const disabledOrLoading = disabled || loading;
    const marginStyle = children
      ? { marginRight: button?.['icon-margin'] }
      : undefined;
    const textOnly = children && !renderIcon;
    const iconOnly = renderIcon && !children;
    const iconAndText = children && renderIcon;
    const iconStyle = variantSwitch<StyleProp>({
      statement: variant,
      variants: {
        [BUTTON_VARIANTS.PRIMARY]: {
          ...(disabled && {
            color: button?.solid?.disabled?.icon?.fill,
          }),
        },

        [BUTTON_VARIANTS.SECONDARY]: {
          color: button?.outlined?.icon?.fill,
          ...(alert && {
            color: button?.outlined?.alert?.icon?.fill,
          }),
          ...(disabled && {
            color: button?.outlined?.disabled?.icon?.fill,
          }),
        },

        [BUTTON_VARIANTS.TERTIARY]: {
          ...(alert && {
            color: button?.flat?.alert?.icon?.fill,
          }),
          ...(disabled && {
            color: button?.flat?.disabled?.icon?.fill,
          }),
        },
      },
    });

    const isTruncated = React.useMemo(
      () => buttonHasWidth({ width: props.width, style }),
      [props.width, style],
    );

    const ellipsisStyle = theme.tokens?.component?.typography?.ellipsis;

    const content = React.useMemo(() => {
      return (
        <ButtonLabel
          $isTruncated={isTruncated}
          $isFullSizeSpinner={loading && !renderIcon}
          $ellipsisStyle={ellipsisStyle}
        >
          {children}
        </ButtonLabel>
      );
    }, [loading, renderIcon, isTruncated, children, ellipsisStyle]);

    return (
      <BasicButton
        type={type}
        tabIndex={disabled ? -1 : undefined}
        {...props}
        className={className}
        ref={ref}
        disabled={disabledOrLoading}
        aria-disabled={disabledOrLoading}
        aria-pressed={selected}
        style={[
          getInteractiveStyles(disabledOrLoading),
          disabledOrLoading && {
            opacity: button?.opacity?.disabled,
          },
          {
            borderRadius: button?.borderRadius,
            fontWeight: button?.fontWeight,
          },
          variantSwitch<StyleProp>({
            statement: variant,
            variants: {
              [BUTTON_VARIANTS.PRIMARY]: {
                backgroundColor: button?.solid?.backgroundColor,
                border: `${button?.borderWidth} solid ${button?.solid?.borderColor}`,
                color: button?.solid?.['text-color'],

                '&:hover': {
                  ...(!disabled &&
                    !loading && {
                      backgroundColor: button?.solid?.hover?.backgroundColor,
                      boxShadow: button?.boxShadow?.hover,
                    }),
                },

                '&:focus:hover': {
                  ...(!disabled &&
                    !loading && {
                      boxShadow: button?.boxShadow?.focus,
                    }),
                },

                '&:active': {
                  backgroundColor: button?.solid?.pressed?.backgroundColor,
                  borderColor: 'transparent',
                  boxShadow: button?.boxShadow?.pressed,

                  ...(loading && {
                    backgroundColor: button?.solid?.loading?.backgroundColor,
                  }),
                  ...(disabled && {
                    backgroundColor: button?.solid?.disabled?.backgroundColor,
                  }),
                },

                ...(loading && {
                  backgroundColor: button?.solid?.loading?.backgroundColor,
                  cursor: 'auto',
                }),

                ...(alert && {
                  backgroundColor: button?.solid?.alert?.backgroundColor,
                  borderColor: 'transparent',
                  color: button?.solid?.alert?.['text-color'],

                  '&:hover': {
                    ...(!disabled &&
                      !loading && {
                        backgroundColor:
                          button?.solid?.alert?.hover?.backgroundColor,
                      }),
                  },

                  '&:active': {
                    backgroundColor:
                      button?.solid?.alert?.pressed?.backgroundColor,
                    borderColor: 'transparent',
                  },

                  ...(loading && {
                    backgroundColor:
                      button?.solid?.alert?.loading?.backgroundColor,
                  }),

                  ...(selected &&
                    !disabled && {
                      backgroundColor:
                        button?.solid?.alert?.selected?.backgroundColor,
                    }),
                }),

                ...(disabled && {
                  backgroundColor: button?.solid?.disabled?.backgroundColor,
                  color: button?.solid?.disabled?.['text-color'],
                }),
              },
              [BUTTON_VARIANTS.SECONDARY]: {
                backgroundColor: 'transparent',
                border: `${button?.borderWidth} solid ${button?.outlined?.borderColor}`,
                color: button?.outlined?.['text-color'],

                '&:hover': {
                  ...(!disabled &&
                    !loading && {
                      boxShadow: button?.boxShadow?.hover,
                    }),
                },

                '&:focus:hover': {
                  ...(!disabled &&
                    !loading && {
                      boxShadow: button?.boxShadow?.focus,
                    }),
                },

                '&:active': {
                  ...(!disabled && {
                    borderColor: button?.outlined?.pressed?.borderColor,
                    boxShadow: button?.boxShadow?.pressed,
                  }),
                },

                ...(alert && {
                  color: button?.outlined?.alert?.['text-color'],
                }),

                ...(disabled && {
                  backgroundColor: 'transparent',
                  borderColor: button?.outlined?.disabled?.borderColor,
                  color: button?.outlined?.disabled?.['text-color'],
                }),

                ...(loading && {
                  cursor: 'auto',
                  color: button?.outlined?.loading?.['text-color'],
                  borderColor: button?.outlined?.loading?.borderColor,
                }),

                ...(selected &&
                  !disabled && {
                    backgroundColor:
                      button?.outlined?.selected?.backgroundColor,
                    borderColor: button?.outlined?.selected?.borderColor,

                    '&:hover': {
                      boxShadow: 'none',
                    },
                    '&:focus': {
                      boxShadow: 'none',
                    },
                    '&:active': {
                      boxShadow: 'none',
                    },
                  }),
              },
              [BUTTON_VARIANTS.TERTIARY]: {
                backgroundColor: 'transparent',
                border: `${button?.borderWidth} solid ${button?.flat?.borderColor}`,
                color: button?.flat?.['text-color'],

                '&:hover': {
                  ...(!disabled &&
                    !loading && {
                      boxShadow: button?.boxShadow?.hover,
                      backgroundColor: button?.flat?.hover?.backgroundColor,
                    }),
                },
                '&:focus': {
                  boxShadow: button?.boxShadow?.focus,
                  backgroundColor: 'transparent',
                },
                '&:focus:hover': {
                  ...(!disabled &&
                    !loading && {
                      boxShadow: button?.boxShadow?.focus,
                      backgroundColor: 'transparent',
                    }),
                },
                '&:active': {
                  ...(!disabled && {
                    borderColor: 'transparent',
                    boxShadow: button?.boxShadow?.pressed,
                    backgroundColor: button?.flat?.pressed?.backgroundColor,
                  }),
                },

                ...(alert && {
                  color: button?.flat?.alert?.['text-color'],
                }),

                ...(disabled && {
                  backgroundColor: 'transparent',
                  color: button?.flat?.disabled?.['text-color'],
                }),

                ...(loading && {
                  cursor: 'auto',
                  color: button?.flat?.loading?.['text-color'],
                  backgroundColor: 'transparent',
                }),

                ...(selected &&
                  !disabled && {
                    backgroundColor: button?.flat?.selected?.backgroundColor,
                    color: button?.flat?.['text-color'],
                    '&:hover': {
                      boxShadow: 'none',
                      backgroundColor: button?.flat?.hover?.backgroundColor,
                    },
                    '&:focus': {
                      boxShadow: 'none',
                      backgroundColor: button?.flat?.selected?.backgroundColor,
                    },
                    '&:active': {
                      boxShadow: 'none',
                      backgroundColor: button?.flat?.selected?.backgroundColor,
                    },
                  }),
              },
            },
          }),
          variantSwitch({
            statement: size,
            variants: {
              [BUTTON_SIZES.SMALL]: {
                fontFamily: button?.fontFamily,
                fontWeight: button?.fontWeight,
                fontSize: button?.small?.fontSize,
                lineHeight: button?.small?.lineHeight,
                height: button?.small?.height,
                ...(textOnly && {
                  padding: button?.small?.['padding-text'],
                }),
                ...(iconOnly && {
                  padding: button?.small?.['padding-icon'],
                }),
                ...(iconAndText && {
                  padding: button?.small?.['padding-icon-text'],
                }),
              },
              [BUTTON_SIZES.MEDIUM]: {
                fontFamily: button?.fontFamily,
                fontWeight: button?.fontWeight,
                fontSize: button?.medium?.fontSize,
                lineHeight: button?.medium?.lineHeight,
                height: button?.medium?.height,
                ...(textOnly && {
                  padding: button?.medium?.['padding-text'],
                }),
                ...(iconOnly && {
                  padding: button?.medium?.['padding-icon'],
                }),
                ...(iconAndText && {
                  padding: button?.medium?.['padding-icon-text'],
                }),
              },
            },
          }),
          style,
        ]}
      >
        {/* If there's a render icon, we change the icon to a progress ring */}
        {loading && renderIcon && (
          <ProgressRing
            style={marginStyle}
            variant={
              variant === BUTTON_VARIANTS.PRIMARY
                ? ProgressRing.VARIANTS.ALTERNATIVE
                : ProgressRing.VARIANTS.DEFAULT
            }
            size={
              size === BUTTON_SIZES.SMALL
                ? ProgressRing.SIZES.XSMALL
                : ProgressRing.SIZES.SMALL
            }
          />
        )}
        {/* If loading and no renderIcon, we need to render the children - but hidden - to keep the correctly sized button */}
        {loading && !renderIcon && (
          <ProgressRing
            style={{
              position: 'absolute',
            }}
            variant={
              variant === BUTTON_VARIANTS.PRIMARY
                ? ProgressRing.VARIANTS.ALTERNATIVE
                : ProgressRing.VARIANTS.DEFAULT
            }
            size={
              size === BUTTON_SIZES.SMALL
                ? ProgressRing.SIZES.XSMALL
                : ProgressRing.SIZES.SMALL
            }
          />
        )}
        {!loading &&
          renderIcon?.({
            size: size === BUTTON_SIZES.SMALL ? 16 : 24,
            style: [marginStyle, iconStyle],
          })}
        {content}
        {renderRightIcon?.({
          size: size === BUTTON_SIZES.SMALL ? 16 : 24,
          style: children ? { marginLeft: button?.['icon-margin'] } : undefined,
        })}
      </BasicButton>
    );
  },
);

Button.displayName = 'Button';

Button.propTypes = {
  /** Button's content */
  children: PropTypes.any,
  /** Styles applied to the root element */
  style: stylePropType,
  /** Class applied to the root element */
  className: PropTypes.string,
  /** button html type, eg: submit */
  type: buttonTypePropType,
  /** Disable button functionality */
  disabled: PropTypes.bool,
  /** handle onclick event for button */
  onClick: PropTypes.func,
  /** render prop to display an icon on the left */
  renderIcon: PropTypes.func,
  /** render prop to display an icon on the right */
  renderRightIcon: PropTypes.func,
  /**  The size of the button, defaults to 'medium' */
  size: PropTypes.oneOf(Object.values(BUTTON_SIZES)),
  /** Deprecated: Adjust width using style instead */
  width: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  /**  The style variant, defaults to 'secondary' */
  variant: PropTypes.oneOf(Object.values(BUTTON_VARIANTS)),
  /** alert mode */
  alert: PropTypes.bool,
  /** is loading state is on */
  loading: PropTypes.bool,
  /** is selected state is on */
  selected: PropTypes.bool,
};

export default Object.assign(Button, {
  VARIANTS: BUTTON_VARIANTS,
  SIZES: BUTTON_SIZES,
});
